import {
  useCallback, useEffect, useLayoutEffect, useRef, useState,
} from 'react';

const NEAR_BOTTOM_THRESHOLD = 450; // px
const LOAD_OLDER_MARGIN = '400px'; // start loading older messages before the top sentinel scrolls into view

/**
 * Manages scrolling behavior for the messages list.
 *
 * - Scrolls to bottom on conversation change (instant) or sent message (smooth)
 * - Auto-scrolls on new messages if already near bottom
 * - Shows "new message" indicator when new messages arrive while scrolled up
 * - Marks conversation as read when scrolling near bottom
 * - Loads older messages when the top sentinel nears view, anchoring the viewport on prepend
 */
function useAutoScroll(messages, conversationId, pendingScrollRef, onMarkRead, { hasMoreOlder, loadOlder }) {
  const [hasNewBelow, setHasNewBelow] = useState(false);
  const [scrollPending, setScrollPending] = useState(false);

  const scrollContainerRef = useRef(null);
  const contentRef = useRef(null);
  const messagesEndRef = useRef(null);
  const topSentinelRef = useRef(null);
  const prevMessageCountRef = useRef(messages.length);
  const prevFirstMessageIdRef = useRef(messages[0]?.id ?? null);
  const lastScrollHeightRef = useRef(0);
  // scrollHeight captured just before an older-page load — lets the prepend keep the
  // viewport anchored where the user was.
  const prependAnchorHeightRef = useRef(0);
  const rafIdRef = useRef(null);

  // Scrolls only the container; `scrollIntoView` would also scroll `overflow: hidden`
  // ancestors (still scrollable per spec) and push the top-of-page banners off-screen.
  const scrollContainerToBottom = (behavior) => {
    const container = scrollContainerRef.current;
    if (!container) return;
    container.scrollTo({ top: container.scrollHeight, behavior });
  };

  const scrollToBottom = useCallback(() => {
    scrollContainerToBottom('smooth');
    setHasNewBelow(false);
    onMarkRead?.();
  }, [onMarkRead]);

  // Dismiss badge and mark as read when user scrolls near the bottom manually
  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    if (distanceFromBottom < NEAR_BOTTOM_THRESHOLD && hasNewBelow) {
      setHasNewBelow(false);
      onMarkRead?.();
    }
  }, [hasNewBelow, onMarkRead]);

  // Load older messages when the top sentinel nears view. scrollHeight is captured first
  // so the prepend layout effect can restore the scroll position.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const sentinel = topSentinelRef.current;
    if (!container || !sentinel || !hasMoreOlder) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          prependAnchorHeightRef.current = container.scrollHeight;
          loadOlder();
        }
      },
      { root: container, rootMargin: `${LOAD_OLDER_MARGIN} 0px 0px 0px` },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [hasMoreOlder, loadOlder]);

  // Auto-follow on content growth (async pitch card, image loads) when near the bottom. Observe the
  // inner content wrapper, not the fixed-height scroll container — the container's own box never grows.
  useEffect(() => {
    const container = scrollContainerRef.current;
    const content = contentRef.current;
    if (!container || !content || typeof ResizeObserver === 'undefined') return undefined;

    lastScrollHeightRef.current = container.scrollHeight;

    const observer = new ResizeObserver(() => {
      const newHeight = container.scrollHeight;
      const oldHeight = lastScrollHeightRef.current;
      if (newHeight <= oldHeight) {
        lastScrollHeightRef.current = newHeight;
        return;
      }
      const wasNearBottom = oldHeight - container.scrollTop - container.clientHeight < NEAR_BOTTOM_THRESHOLD;
      lastScrollHeightRef.current = newHeight;
      if (wasNearBottom) {
        scrollContainerToBottom('instant');
      }
    });

    observer.observe(content);
    return () => observer.disconnect();
  }, []);

  // Hide container immediately when conversation changes so stale messages aren't visible
  useLayoutEffect(() => {
    setScrollPending(true);
    setHasNewBelow(false);
    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
    };
  }, [conversationId]);

  // Scroll to bottom after messages render.
  // useMessages sets pendingScrollRef to "instant" (conversation change) or "smooth" (sent message).
  // For realtime updates from others, we only scroll if already near the bottom.
  // An older-page prepend keeps the viewport anchored instead.
  useLayoutEffect(() => {
    if (messages.length === 0) {
      prevMessageCountRef.current = 0;
      prevFirstMessageIdRef.current = null;
      setScrollPending(false);
      return;
    }

    const firstId = messages[0]?.id ?? null;
    const grew = messages.length > prevMessageCountRef.current;
    // Prepend: the list grew and its first item changed — older messages loaded above.
    const isPrepend = grew && prevFirstMessageIdRef.current !== null && firstId !== prevFirstMessageIdRef.current;

    const pendingScroll = pendingScrollRef.current;
    if (pendingScroll) {
      // eslint-disable-next-line no-param-reassign -- consuming the pending scroll signal
      pendingScrollRef.current = false;
      scrollContainerToBottom(pendingScroll);
      // Reveal after the browser paints the scrolled position
      rafIdRef.current = requestAnimationFrame(() => {
        rafIdRef.current = null;
        setScrollPending(false);
      });
      setHasNewBelow(false);
      prevMessageCountRef.current = messages.length;
      prevFirstMessageIdRef.current = firstId;
      return;
    }

    if (isPrepend) {
      // Older messages were prepended — shift down by their height so the viewport stays put.
      const container = scrollContainerRef.current;
      if (container && prependAnchorHeightRef.current) {
        container.scrollTop += container.scrollHeight - prependAnchorHeightRef.current;
      }
      prevMessageCountRef.current = messages.length;
      prevFirstMessageIdRef.current = firstId;
      return;
    }

    const container = scrollContainerRef.current;
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
    const isNewMessage = grew;
    prevMessageCountRef.current = messages.length;
    prevFirstMessageIdRef.current = firstId;

    if (distanceFromBottom < NEAR_BOTTOM_THRESHOLD) {
      scrollContainerToBottom('smooth');
      setHasNewBelow(false);
      if (isNewMessage) onMarkRead?.();
    } else if (isNewMessage) {
      setHasNewBelow(true);
    }
  }, [messages, pendingScrollRef, onMarkRead]);

  return {
    scrollContainerRef,
    contentRef,
    messagesEndRef,
    topSentinelRef,
    hasNewBelow,
    scrollPending,
    scrollToBottom,
    handleScroll,
  };
}

export default useAutoScroll;
